import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface MonthCell {
  net: number | null;
  newFollowers: number | null;
  unfollows: number | null;
}
interface AccountData {
  brand: string;
  platform: string;
  label: string;
  byMonth: Record<string, MonthCell>;
}
interface BonusQuarter {
  label: string;
  months: string[];
  value: number;
  payout: number;
  status: "complete" | "in_progress" | "upcoming";
  nextTier: { min: number; payout: number; needed: number } | null;
}
interface BonusTier {
  min: number;
  payout: number;
}
interface FollowerBonus {
  accountLabel?: string;
  tiers: BonusTier[];
  quarters: BonusQuarter[];
}
interface LeadBonus {
  sources: {
    label: string;
    byMonth: Record<string, number>;
    dealsByMonth?: Record<string, number>;
  }[];
  tiers: BonusTier[];
  quarters: BonusQuarter[];
}
interface CloseRateQuarter extends BonusQuarter {
  leads: number;
  deals: number;
}
interface CloseRateBonus {
  tiers: BonusTier[];
  quarters: CloseRateQuarter[];
}
export interface SocialFollowersResponse {
  fiscalYearLabel?: string;
  months?: string[];
  accounts: AccountData[];
  bonus?: FollowerBonus | null;
  leadBonus?: LeadBonus | null;
  closeRateBonus?: CloseRateBonus | null;
}

const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");
const fmtNet = (n: number | null) => (n === null ? "—" : `${n > 0 ? "+" : ""}${fmtInt(n)}`);
const fmtUsd = (n: number) => `$${n.toLocaleString("en-US")}`;
const fmtPct = (n: number) => `${Math.round(n * 100)}%`;
function fmtMonth(month: string) {
  const [y = 0, m = 1] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}
function netClass(n: number | null) {
  if (n === null) return "text-muted-foreground";
  return n < 0 ? "text-red-600" : "";
}

/**
 * Loads an owner's social-media bonus data. Resolves to null for people
 * without social accounts configured (most people), so callers can use it to
 * decide whether to show the card at all.
 */
export function useSocialBonuses(ownerId: string): SocialFollowersResponse | null {
  const [data, setData] = useState<SocialFollowersResponse | null>(null);
  useEffect(() => {
    setData(null);
    fetch(`/api/owners/${ownerId}/social-followers`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: SocialFollowersResponse | null) =>
        setData(d && d.accounts.length > 0 && d.months ? d : null),
      )
      .catch(() => setData(null));
  }, [ownerId]);
  return data;
}

/**
 * Social media bonuses for people whose pay tracks social growth: quarterly
 * follower and lead bonuses plus the monthly numbers behind them.
 */
export function SocialFollowersCard({ data }: { data: SocialFollowersResponse }) {
  if (!data.months) return null;
  const { months, accounts, bonus, leadBonus, closeRateBonus } = data;
  const leadSources = leadBonus?.sources ?? [];

  const hasAnyData = (month: string) =>
    accounts.some((a) => a.byMonth[month]?.net != null) ||
    leadSources.some((s) => (s.byMonth[month] ?? 0) > 0);
  const dealSources = leadSources.filter((s) => s.dealsByMonth);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Social Media Bonuses ({data.fiscalYearLabel})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {leadBonus && (
          <QuarterlyBonusTable
            title="Lead Generation Bonus — Instagram + FP TikTok leads"
            unit="leads"
            tiers={leadBonus.tiers}
            quarters={leadBonus.quarters}
            breakdown={leadSources.map((s) => ({
              label: s.label,
              valueFor: (q) => q.months.reduce((sum, m) => sum + (s.byMonth[m] ?? 0), 0),
            }))}
          />
        )}
        {closeRateBonus && (
          <QuarterlyBonusTable
            title="Close Rate Bonus — Instagram + FP TikTok deals ÷ leads"
            unit="close rate"
            valueLabel="Close Rate"
            fmtValue={fmtPct}
            fmtThreshold={fmtPct}
            tiers={closeRateBonus.tiers}
            quarters={closeRateBonus.quarters}
            breakdown={[
              { label: "Leads", valueFor: (q) => (q as CloseRateQuarter).leads },
              { label: "Deals", valueFor: (q) => (q as CloseRateQuarter).deals },
            ]}
            shortfall={(q, t, complete) => {
              const { leads, deals } = q as CloseRateQuarter;
              // Deals needed at the current lead count to reach this rate.
              if (leads === 0) return "—";
              const needed = Math.max(1, Math.ceil(t.min * leads - deals - 1e-9));
              const noun = needed === 1 ? "deal" : "deals";
              return complete ? `${needed} ${noun} short` : `${needed} more ${noun}`;
            }}
          />
        )}
        {bonus && (
          <QuarterlyBonusTable
            title={`Follower Bonus — ${bonus.accountLabel} net followers only`}
            unit="net"
            signed
            tiers={bonus.tiers}
            quarters={bonus.quarters}
          />
        )}

        <div>
          <h3 className="text-sm font-semibold">Monthly Detail</h3>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th />
                  <th
                    colSpan={accounts.length}
                    className="pt-1 px-2 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    Net Followers
                  </th>
                  {leadSources.length > 0 && (
                    <th
                      colSpan={leadSources.length}
                      className="pt-1 px-2 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground border-l"
                    >
                      Leads
                    </th>
                  )}
                  {dealSources.length > 0 && (
                    <th
                      colSpan={dealSources.length}
                      className="pt-1 px-2 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground border-l"
                    >
                      Deals Closed
                    </th>
                  )}
                </tr>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Month</th>
                  {accounts.map((a) => (
                    <th
                      key={a.label}
                      className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap"
                    >
                      {a.label}
                    </th>
                  ))}
                  {leadSources.map((s, i) => (
                    <th
                      key={s.label}
                      className={cn(
                        "text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap",
                        i === 0 && "border-l",
                      )}
                    >
                      {s.label}
                    </th>
                  ))}
                  {dealSources.map((s, i) => (
                    <th
                      key={s.label}
                      className={cn(
                        "text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap",
                        i === 0 && "border-l",
                      )}
                    >
                      {s.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <tr
                    key={m}
                    className={cn("border-b last:border-0", !hasAnyData(m) && "opacity-40")}
                  >
                    <td className="py-2 pr-4 font-medium whitespace-nowrap">{fmtMonth(m)}</td>
                    {accounts.map((a) => {
                      const cell = a.byMonth[m];
                      const breakdown =
                        cell?.newFollowers != null && cell.unfollows != null
                          ? `${cell.newFollowers} new − ${cell.unfollows} unfollows`
                          : undefined;
                      return (
                        <td
                          key={a.label}
                          title={breakdown}
                          className={cn(
                            "text-right py-2 px-2 tabular-nums",
                            netClass(cell?.net ?? null),
                          )}
                        >
                          {fmtNet(cell?.net ?? null)}
                        </td>
                      );
                    })}
                    {leadSources.map((s, i) => (
                      <td
                        key={s.label}
                        className={cn("text-right py-2 px-2 tabular-nums", i === 0 && "border-l")}
                      >
                        {s.byMonth[m] ? fmtInt(s.byMonth[m] ?? 0) : "—"}
                      </td>
                    ))}
                    {dealSources.map((s, i) => (
                      <td
                        key={s.label}
                        className={cn("text-right py-2 px-2 tabular-nums", i === 0 && "border-l")}
                      >
                        {s.dealsByMonth?.[m] ? fmtInt(s.dealsByMonth[m] ?? 0) : "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Net followers = new follows − unfollows; Instagram syncs daily, TikTok comes from
            Marketing → Social Media → FP TikTok. Leads = HubSpot quote forms filled with source
            Instagram or TikTok (Diamond Dude TikTok not included). Deals closed = orders placed
            that month from those sources; close rate = a quarter's deals ÷ its leads.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

const STATUS_LABEL: Record<BonusQuarter["status"], string> = {
  complete: "Final",
  in_progress: "In progress",
  upcoming: "Upcoming",
};

// One quarterly bonus: the quarter's total (optionally split by source),
// the payout tier it lands in, and how far the current quarter is from the
// next tier.
function QuarterlyBonusTable({
  title,
  unit,
  signed = false,
  tiers,
  quarters,
  breakdown = [],
  valueLabel,
  fmtValue: fmtValueProp,
  fmtThreshold = fmtInt,
  shortfall,
}: {
  title: string;
  unit: string;
  signed?: boolean;
  tiers: BonusTier[];
  quarters: BonusQuarter[];
  breakdown?: { label: string; valueFor: (q: BonusQuarter) => number }[];
  /** Header for the graded value column (defaults to Total / Net Followers). */
  valueLabel?: string;
  fmtValue?: (n: number) => string;
  fmtThreshold?: (n: number) => string;
  /** Text for a missed tier; defaults to "N to go" / "N short". */
  shortfall?: (q: BonusQuarter, tier: BonusTier, complete: boolean) => string;
}) {
  const lowest = tiers[tiers.length - 1];
  // Goal columns read left-to-right from the first tier up.
  const tiersAsc = [...tiers].sort((a, b) => a.min - b.min);
  const fmtValue = fmtValueProp ?? ((n: number) => (signed ? fmtNet(n) : fmtInt(n)));
  const missedText = (q: BonusQuarter, t: BonusTier) => {
    const complete = q.status === "complete";
    if (shortfall) return shortfall(q, t, complete);
    const short = fmtInt(t.min - q.value);
    return complete ? `${short} short` : `${short} to go`;
  };
  return (
    <div className="border-b pb-4">
      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        {tiers.map((t) => `${fmtThreshold(t.min)}+ ${unit} → ${fmtUsd(t.payout)}`).join(" · ")}
        {lowest && ` · below ${fmtThreshold(lowest.min)} → no bonus`}
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Quarter</th>
              {breakdown.map((b) => (
                <th
                  key={b.label}
                  className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap"
                >
                  {b.label}
                </th>
              ))}
              <th className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap">
                {valueLabel ?? (breakdown.length > 0 ? "Total" : "Net Followers")}
              </th>
              <th className="text-right py-2 px-2 font-medium text-muted-foreground">Bonus</th>
              {tiersAsc.map((t) => (
                <th
                  key={t.min}
                  className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap align-bottom"
                >
                  <div>{fmtUsd(t.payout)} goal</div>
                  <div className="text-[11px] font-normal text-brand-gold">
                    {fmtThreshold(t.min)}+ {unit}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {quarters.map((q) => {
              const upcoming = q.status === "upcoming";
              const span = q.months.map((m) => fmtMonth(m).split(" ")[0]);
              return (
                <tr
                  key={q.label}
                  className={cn("border-b last:border-0", upcoming && "opacity-40")}
                >
                  <td className="py-2 pr-4 font-medium whitespace-nowrap">
                    <div>
                      {q.label}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {span[0]}–{span[span.length - 1]}
                      </span>
                    </div>
                    <div className="text-[11px] font-normal text-muted-foreground">
                      {STATUS_LABEL[q.status]}
                    </div>
                  </td>
                  {breakdown.map((b) => (
                    <td key={b.label} className="text-right py-2 px-2 tabular-nums">
                      {upcoming ? "—" : fmtInt(b.valueFor(q))}
                    </td>
                  ))}
                  <td
                    className={cn(
                      "text-right py-2 px-2 tabular-nums font-medium",
                      signed && netClass(q.value),
                    )}
                  >
                    {upcoming ? "—" : fmtValue(q.value)}
                  </td>
                  <td
                    className={cn(
                      "text-right py-2 px-2 tabular-nums font-semibold whitespace-nowrap",
                      q.payout > 0 ? "text-green-600" : "text-muted-foreground",
                    )}
                  >
                    {upcoming ? "—" : q.payout > 0 ? fmtUsd(q.payout) : "$0"}
                  </td>
                  {tiersAsc.map((t) => {
                    const hit = q.value >= t.min;
                    return (
                      <td
                        key={t.min}
                        className={cn(
                          "text-right py-2 px-2 tabular-nums whitespace-nowrap",
                          hit ? "text-green-600 font-semibold" : "text-muted-foreground",
                        )}
                      >
                        {upcoming ? "—" : hit ? "✓" : missedText(q, t)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
